import { z } from "zod";

import { HQ_PERMISSIONS, HQ_ROLES } from "../rbac/permissions.js";

/**
 * Hợp đồng đăng nhập của hub.
 *
 * **Không có `register`.** Đây không phải thiếu sót chờ bổ sung: hub chỉ dành cho nhân viên nội bộ,
 * tài khoản do chủ cấp. Cổng `G-no-public-signup` giữ cho một lần « thêm tạm cho tiện » không mọc
 * lại thành một cửa đăng ký công khai vào trung tâm kiểm soát.
 */

export const LoginRequestSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(12, "Mật khẩu phải từ 12 ký tự"),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

export const MeResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.email(),
    displayName: z.string(),
  }),
  role: z.enum(HQ_ROLES),
  permissions: z.array(z.enum(HQ_PERMISSIONS)),
  /**
   * Còn `true` thì mọi route khác trả `HQ_PASSWORD_CHANGE_REQUIRED`.
   *
   * Ép ở MÁY CHỦ, không chỉ chuyển hướng ở giao diện: một trang bị chuyển hướng vẫn gọi API được
   * bằng `curl`, và mật khẩu tạm thì đã đi qua stdout — nơi terminal và trình gom log có thể giữ lại.
   */
  mustChangePassword: z.boolean(),
  /** Hạn của mật khẩu tạm. `null` khi đã đổi. */
  temporaryCredentialExpiresAt: z.iso.datetime().nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const LoginResponseSchema = MeResponseSchema;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const LoginContract = {
  id: "hq.identity.login",
  version: "1.0.0",
  request: LoginRequestSchema,
  response: LoginResponseSchema,
} as const;

export const MeContract = {
  id: "hq.identity.me",
  version: "1.0.0",
  request: z.object({}),
  response: MeResponseSchema,
} as const;

export const ChangePasswordContract = {
  id: "hq.identity.change-password",
  version: "1.0.0",
  request: ChangePasswordRequestSchema,
  response: MeResponseSchema,
} as const;

/**
 * Bốn đường DUY NHẤT một phiên chưa đổi mật khẩu được đi.
 *
 * Khai ở hợp đồng chứ không rải trong guard: đây là danh sách mà cả máy chủ lẫn giao diện phải đồng
 * ý, và hai bản sao lệch nhau nghĩa là người dùng bị khoá ngoài hoặc lọt vào.
 */
export const PASSWORD_CHANGE_ALLOWED_PATHS = [
  "/internal/auth/me",
  "/internal/auth/change-password",
  "/internal/auth/logout",
  "/internal/auth/refresh",
] as const;

/**
 * Nhân viên nội bộ — `/admin/staff`.
 *
 * ── `status` là tư cách thành viên HQ, KHÔNG phải trạng thái danh tính ──────
 *
 * `iam.users` dùng chung nhiều app nội bộ. Một người bị khoá khỏi hub vẫn có thể đang dùng app khác
 * bình thường — hiển thị « đã khoá » ở đây mà không nói rõ phạm vi sẽ khiến người đọc tưởng tài
 * khoản toàn cục của họ đã bị vô hiệu.
 */
export const StaffRowSchema = z.object({
  userId: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: z.enum(HQ_ROLES).nullable(),
  /** Tư cách thành viên trong workspace nội bộ của hub. */
  membershipStatus: z.string(),
  /** Còn đang dùng mật khẩu tạm và bị ép đổi. */
  mustChangePassword: z.boolean(),
  createdAt: z.iso.datetime().nullable(),
});
export type StaffRow = z.infer<typeof StaffRowSchema>;

export const StaffListResponseSchema = z.object({ rows: z.array(StaffRowSchema) });
export type StaffListResponse = z.infer<typeof StaffListResponseSchema>;

export const InviteStaffRequestSchema = z.object({
  email: z.email(),
  displayName: z.string().min(1).max(200),
  role: z.enum(HQ_ROLES),
});
export type InviteStaffRequest = z.infer<typeof InviteStaffRequestSchema>;

/**
 * Mật khẩu tạm trả về ĐÚNG MỘT LẦN.
 *
 * Nó không được lưu ở đâu khác ngoài dạng băm, nên nếu người cấp không chép lại ngay thì phải cấp
 * lại. Hợp đồng nói rõ điều đó bằng tên trường, không phải bằng một dòng tài liệu.
 */
export const InviteStaffResponseSchema = z.object({
  userId: z.string(),
  temporaryPasswordShownOnce: z.string(),
  expiresAt: z.iso.datetime().nullable(),
});
export type InviteStaffResponse = z.infer<typeof InviteStaffResponseSchema>;

export const ChangeRoleRequestSchema = z.object({ role: z.enum(HQ_ROLES) });
export type ChangeRoleRequest = z.infer<typeof ChangeRoleRequestSchema>;
